import { useTranslation as useI18nTranslation } from 'react-i18next';
import type { i18n } from 'i18next';
import { type Language } from './config';
import type { TranslationKeys } from './types';

type Namespace = 'common';
export type TranslationKey =
  | `common.${keyof TranslationKeys['common']}`
  | `validation.${keyof TranslationKeys['validation']}`
  | `qualityMessages.${keyof TranslationKeys['qualityMessages']}`
  | `dates.${keyof TranslationKeys['dates']}`
  | `sidebar.${keyof TranslationKeys['sidebar']}`
  | `header.${keyof TranslationKeys['header']}`
  | `feedback.${keyof TranslationKeys['feedback']}`
  | `jobSteps.${keyof TranslationKeys['jobSteps']}`
  | `getStarted.${keyof TranslationKeys['getStarted']}`
  | `contentImpact.${keyof TranslationKeys['contentImpact']}`
  | `productsView.${keyof TranslationKeys['productsView']}`
  | `plans.${keyof TranslationKeys['plans']}`
  | `dashboard.${keyof TranslationKeys['dashboard']}`
  | `priorities.${keyof TranslationKeys['priorities']}`
  | `helpTopics.${keyof TranslationKeys['helpTopics']}`
  | `jobsView.${keyof TranslationKeys['jobsView']}`
  | `productStates.${keyof TranslationKeys['productStates']}`
  | `enrichmentConfig.${keyof TranslationKeys['enrichmentConfig']}`
  | `improvementSection.${keyof TranslationKeys['improvementSection']}`
  | `editPanel.${keyof TranslationKeys['editPanel']}`
  | `optimizationDashboard.${keyof TranslationKeys['optimizationDashboard']}`
  | `optimizationTesting.${keyof TranslationKeys['optimizationTesting']}`
  | `usageTable.${keyof TranslationKeys['usageTable']}`
  | `storeDialog.${keyof TranslationKeys['storeDialog']}`
  | `excelReimportDialog.${keyof TranslationKeys['excelReimportDialog']}`
  | `getStartedDialog.${keyof TranslationKeys['getStartedDialog']}`
  | `reconnectDialog.${keyof TranslationKeys['reconnectDialog']}`
  | `startEnrichmentDialog.${keyof TranslationKeys['startEnrichmentDialog']}`
  | `startImportingDialog.${keyof TranslationKeys['startImportingDialog']}`
  | `switchStoreDialog.${keyof TranslationKeys['switchStoreDialog']}`
  | `productCardContent.${keyof TranslationKeys['productCardContent']}`
  | `seoKeywordsSection.${keyof TranslationKeys['seoKeywordsSection']}`
  | `jobsEmptyView.${keyof TranslationKeys['jobsEmptyView']}`
  | `imageGallery.${keyof TranslationKeys['imageGallery']}`
  | `exportButton.${keyof TranslationKeys['exportButton']}`
  | `exportControls.${keyof TranslationKeys['exportControls']}`
  | `altTextImprovements.${keyof TranslationKeys['altTextImprovements']}`
  | `productsQualityOverview.${keyof TranslationKeys['productsQualityOverview']}`
  | `scoreBreakdownTable.${keyof TranslationKeys['scoreBreakdownTable']}`
  | `sharedSuggestionLoading.${keyof TranslationKeys['sharedSuggestionLoading']}`
  | `promptSelector.${keyof TranslationKeys['promptSelector']}`
  | `metaFields.${keyof TranslationKeys['metaFields']}`
  | `marketRadar.${keyof TranslationKeys['marketRadar']}`
  | `placeSelection.${keyof TranslationKeys['placeSelection']}`
  | `onboarding.${keyof TranslationKeys['onboarding']}`
  | `auth.${keyof TranslationKeys['auth']}`;

interface TranslationOptions {
  lng?: Language;
  ns?: Namespace | Namespace[];
}

interface TranslationReturn {
  t: (key: TranslationKey, options?: Record<string, unknown>) => string;
  i18n: i18n;
  changeLanguage: (language: Language) => Promise<void>;
  getCurrentLanguage: () => Language;
}

export const useTranslation = (options?: TranslationOptions): TranslationReturn => {
  const { t, i18n } = useI18nTranslation(options?.ns);

  const changeLanguage = async (language: Language): Promise<void> => {
    await i18n.changeLanguage(language);
  };

  const getCurrentLanguage = (): Language => {
    return i18n.language as Language;
  };

  return {
    t: t as TranslationReturn['t'],
    i18n,
    changeLanguage,
    getCurrentLanguage,
  };
};

export const formatDate = (date: Date | string, format: string): string => {
  // We'll implement this with date-fns in the next step
  return new Date(date).toLocaleDateString();
};

export const formatNumber = (
  number: number,
  options?: Intl.NumberFormatOptions
): string => {
  const { getCurrentLanguage } = useTranslation();
  const language = getCurrentLanguage();
  return new Intl.NumberFormat(language, options).format(number);
};
